import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentfulImage } from '../Contentful';
import { images } from '../shared/assets';
// Navigation now handled by parent component
import '../HeroSection/HeroSection.css';

interface PropertyHeroSectionProps {
  propertySlug: string;
  videoUrl?: string;
  customContent?: {
    title?: string;
    heading?: string;
    copy?: string;
  };
}

const PropertyHeroSection: React.FC<PropertyHeroSectionProps> = ({ propertySlug, videoUrl, customContent }) => {
  const { t } = useTranslation();
  const [heroAnimated, setHeroAnimated] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  // Trigger hero animations on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setHeroAnimated(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Get video URL from assets based on property slug
  const getPropertyVideoUrl = (slug: string) => {
    const propertyAssets = {
      'atacama': images.atacamaPage?.heroBackground,
      'patagonia': images.patagoniaPage?.heroBackground,
      'iguazu': images.iguazuPage?.heroBackground,
      'mendoza': images.mendozaPage?.heroBackground,
      'santa-catarina': images.santaCatarinaPage?.heroBackground,
    };
    return propertyAssets[slug as keyof typeof propertyAssets];
  };
  
  // Use Contentful video URL from assets, fallback to provided URL
  const contentfulVideoUrl = getPropertyVideoUrl(propertySlug);
  const finalVideoUrl = contentfulVideoUrl || videoUrl;
  const heroBackground = finalVideoUrl || images.heroBackground;
  const isVideo = !!finalVideoUrl;
  
  console.log('PropertyHeroSection - propertySlug:', propertySlug, 'contentfulVideoUrl:', contentfulVideoUrl, 'fallbackVideoUrl:', videoUrl, 'finalVideoUrl:', finalVideoUrl, 'isVideo:', isVideo);

  if (isVideo) {
    return (
      <div className="hero-section" style={{ position: 'relative', overflow: 'hidden' }}>
        <video 
          className="hero-video"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0
          }}
          src={finalVideoUrl}
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={() => console.log('Video loaded:', finalVideoUrl)}
          onError={(e) => console.error('Video error:', finalVideoUrl, e)}
        />

        {/* Navigation handled by parent component */}

        {/* Hero Content */}
        <div className="hero-content-wrapper">
          <h1 className={`hero-title ${heroAnimated ? 'animated' : ''}`}>
            {customContent?.title || t(`properties/${propertySlug}:hero.title`)}
          </h1>
          {(customContent?.heading || t(`properties/${propertySlug}:hero.heading`, '')) && (
            <h2 className={`hero-heading text-display ${heroAnimated ? 'animated' : ''}`}>
              {customContent?.heading || t(`properties/${propertySlug}:hero.heading`)}
            </h2>
          )}
          {(customContent?.copy !== undefined ? customContent.copy : t(`properties/${propertySlug}:hero.description`, '')) && (
            <p className={`hero-description text-body-large ${heroAnimated ? 'animated' : ''}`}>
              {customContent?.copy !== undefined ? customContent.copy : t(`properties/${propertySlug}:hero.description`)}
            </p>
          )}
        </div>
        
        
        <button className="volume-button" aria-label={t(`properties/${propertySlug}:hero.volumeAria`)}>
          <ContentfulImage 
            imageKey="icon-volume" 
            fallbackSrc={images.volumeIcon} 
            alt={t(`properties/${propertySlug}:hero.volumeAria`)} 
          />
        </button>
      </div>
    );
  }

  // Fallback to image background (same as original HeroSection)
  return (
    <div 
      className="hero-section" 
      style={{ 
        backgroundImage: `linear-gradient(90deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.3) 100%), url('${heroBackground}')` 
      }}
    >
      {/* Navigation handled by parent component */}

      {/* Hero Content */}
      <div className="hero-content-wrapper">
        <h1 className={`hero-title ${heroAnimated ? 'animated' : ''}`}>
          {customContent?.title || t(`properties/${propertySlug}:hero.title`)}
        </h1>
        {(customContent?.heading || t(`properties/${propertySlug}:hero.heading`, '')) && (
          <h2 className={`hero-heading text-display ${heroAnimated ? 'animated' : ''}`}>
            {customContent?.heading || t(`properties/${propertySlug}:hero.heading`)}
          </h2>
        )}
        {(customContent?.copy !== undefined ? customContent.copy : t(`properties/${propertySlug}:hero.description`, '')) && (
          <p className={`hero-description text-body-large ${heroAnimated ? 'animated' : ''}`}>
            {customContent?.copy !== undefined ? customContent.copy : t(`properties/${propertySlug}:hero.description`)}
          </p>
        )}
      </div>
      
      
      <button className="volume-button" aria-label={t(`properties/${propertySlug}:hero.volumeAria`)}>
        <ContentfulImage imageKey="icon-volume" fallbackSrc={images.volumeIcon} alt={t(`properties/${propertySlug}:hero.volumeAria`)} />
      </button>
    </div>
  );
};

export default PropertyHeroSection;